// Custom sorting for Material Table and shared comparators used by TanStack tables.

import { Status } from '../types/redux/data/dataTypes';
import { specifyStatus } from './generalMappings';

/** Options for empty-last sorting (Material `customSort` and TanStack `sortingFn`). */
export interface SortOptions {
  emptyLast?: boolean;
  /** `desc` inverts empty-last placement so empties stay last in both directions. */
  direction?: 'asc' | 'desc' | string;
}

export function isEmptySortValue(value: unknown): boolean {
  if (value == null || value === '' || value === '-') return true;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'n/a' || normalized === 'na' || normalized === 'none') {
      return true;
    }
  }
  return false;
}

export function isEmptyNumericSortValue(value: unknown): boolean {
  if (isEmptySortValue(value)) return true;
  return typeof value === 'number' && Number.isNaN(value);
}

/**
 * Empty cells sort after non-empty in both asc and desc.
 * TanStack: pre-invert on `desc` and set `sortUndefined: false` on the column.
 */
export function compareEmptyLast(
  aEmpty: boolean,
  bEmpty: boolean,
  options?: SortOptions,
): number {
  if (!options?.emptyLast) return 0;
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return options.direction === 'desc' ? -1 : 1;
  if (bEmpty) return options.direction === 'desc' ? 1 : -1;
  return 0;
}

/** Lower rank sorts earlier (asc): Fail → Alert → Pass; empty/NA last. */
export function metricStatusRank(value: unknown): number {
  if (isEmptySortValue(value)) return 99;
  const status = specifyStatus(String(value));
  const rank: Partial<Record<Status, number>> = {
    [Status.Fail]: 1,
    [Status.Alert]: 2,
    [Status.PassAlert]: 3,
    [Status.Pass]: 4,
    [Status.NA]: 0,
  };
  return rank[status] ?? 50;
}

function compareMetricStatusValues<T>(
  a: T,
  b: T,
  key: keyof T,
  additionalKey?: keyof T,
): number {
  const aValue = a[key];
  const bValue = b[key];
  const aRank = metricStatusRank(aValue);
  const bRank = metricStatusRank(bValue);

  if (aRank !== bRank) {
    return aRank - bRank;
  }

  if (additionalKey) {
    const aVal = a[additionalKey] as number;
    const bVal = b[additionalKey] as number;
    if (aVal > bVal) return -1;
    if (aVal < bVal) return 1;
  }

  return 0;
}

/**
 * Material Table `customSort` for Pass/Alert/Fail status columns.
 */
export function statusSort<T>(
  a: T,
  b: T,
  key: keyof T,
  additionalKey?: keyof T,
  options?: SortOptions,
): number {
  const emptyCompare = compareEmptyLast(
    isEmptySortValue(a[key]),
    isEmptySortValue(b[key]),
    options,
  );
  if (emptyCompare !== 0) return emptyCompare;
  return compareMetricStatusValues(a, b, key, additionalKey);
}

/**
 * Material Table `customSort` for fund names (e.g. `mersenne001`, `adepa9999_01`).
 */
export function sortFundNames(a: string, b: string): number {
  const aMatch = a.match(/([a-zA-Z]+)(\d+)(.*)/) || [];
  const bMatch = b.match(/([a-zA-Z]+)(\d+)(.*)/) || [];

  if (!aMatch[1] || !aMatch[2]) {
    if (!bMatch[1] || !bMatch[2]) {
      return a.localeCompare(b);
    }
    return 1;
  }
  if (!bMatch[1] || !bMatch[2]) {
    return -1;
  }

  const textCompare = aMatch[1].localeCompare(bMatch[1]);
  if (textCompare !== 0) return textCompare;

  const numCompare = parseInt(aMatch[2]) - parseInt(bMatch[2]);
  if (numCompare !== 0) return numCompare;

  return (aMatch[3] || '').localeCompare(bMatch[3] || '');
}
